import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  User, QrCode, Percent, History, LogOut, Menu, X,
  Coins, CheckCircle2, Clock, XCircle, Store, MapPin, Gift, Ticket, ExternalLink,
  Users, Copy, Share2, Home, Plus
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import Logo from '../components/Logo';

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [offers, setOffers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [myCoupons, setMyCoupons] = useState([]);
  const [household, setHousehold] = useState(null);
  const [allCondos, setAllCondos] = useState([]);
  const [addResidenceOpen, setAddResidenceOpen] = useState(false);
  const [selectedNewCondo, setSelectedNewCondo] = useState('');
  const [expenses, setExpenses] = useState([]);
  const [redeemDialog, setRedeemDialog] = useState({ open: false, expense: null });
  const [redeemPoints, setRedeemPoints] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const redeemRequest = useRef(null);
  const [pointsCfg, setPointsCfg] = useState({ user_point_value: 0.07 });
  const [availablePoints, setAvailablePoints] = useState(0);
  const [couponQrView, setCouponQrView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [offersRes, transactionsRes, couponsRes, householdRes, condosRes, expensesRes, pointsRes, meRes] = await Promise.all([
        api.get('/offers'),
        api.get('/transactions'),
        api.get('/my-coupons').catch(() => ({ data: [] })),
        api.get('/household/me').catch(() => ({ data: null })),
        api.get('/public/condominiums').catch(() => ({ data: [] })),
        api.get('/user/expenses').catch(() => ({ data: [] })),
        api.get('/settings/points').catch(() => ({ data: { user_point_value: 0.07 } })),
        api.get('/auth/me').catch(() => ({ data: null })),
      ]);
      setOffers(offersRes.data);
      setTransactions(transactionsRes.data);
      setMyCoupons(couponsRes.data || []);
      setHousehold(householdRes.data);
      setAllCondos(condosRes.data || []);
      setExpenses(expensesRes.data || []);
      setPointsCfg(pointsRes.data);
      if (meRes.data) setAvailablePoints(meRes.data.total_cashback || 0);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openRedeem = (expense) => {
    redeemRequest.current = { id: crypto.randomUUID(), points: null };
    setRedeemDialog({ open: true, expense });
    const upv = pointsCfg.user_point_value || 0.07;
    const dueEur = expense.amount_due_eur;
    const maxPointsForDue = upv > 0 ? Math.ceil(dueEur / upv) : 0;
    const suggested = Math.min(Math.floor(availablePoints), maxPointsForDue);
    setRedeemPoints(suggested > 0 ? String(suggested) : '');
  };

  const handleRedeem = async () => {
    if (redeeming) return;
    const points = parseFloat(redeemPoints);
    if (!Number.isFinite(points) || points <= 0) {
      toast.error('Inserisci un numero di punti valido');
      return;
    }
    if (redeemRequest.current?.points !== points) redeemRequest.current = { id: crypto.randomUUID(), points };
    setRedeeming(true);
    try {
      const res = await api.post(`/user/expenses/${redeemDialog.expense.id}/redeem`, { points, request_id: redeemRequest.current.id });
      toast.success(`Applicati ${res.data.points_applied - (redeemDialog.expense.points_applied || 0)} punti = €${(res.data.amount_paid_eur - (redeemDialog.expense.amount_paid_eur || 0)).toFixed(2)}`);
      setRedeemDialog({ open: false, expense: null });
      setRedeemPoints('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'applicazione dei punti');
    } finally {
      setRedeeming(false);
    }
  };

  const handleAddResidence = async () => {
    if (!selectedNewCondo) return;
    try {
      await api.post('/users/me/residences', { condominium_id: selectedNewCondo });
      toast.success('Richiesta inviata! Attendi l\'approvazione dell\'admin del condominio.');
      setAddResidenceOpen(false);
      setSelectedNewCondo('');
      // refresh user (to fetch updated condominium_ids)
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const userCondoIds = user?.condominium_ids || (user?.condominium_id ? [user.condominium_id] : []);
  const userCondoApprovals = user?.condo_approvals || {};

  const copyReferralLink = () => {
    if (!household?.referral_code) return;
    const link = `${window.location.origin}/register?ref=${household.referral_code}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copiato! Invia ai tuoi familiari');
  };

  const handleSaveCoupon = async (offerId) => {
    try {
      await api.post(`/coupons/save/${offerId}`);
      toast.success('Punti attivati!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const handleConfirmTransaction = async (transactionId) => {
    try {
      await api.put(`/transactions/${transactionId}/confirm`);
      toast.success('Transazione confermata! Punti accreditati.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella conferma');
    }
  };

  const handleRejectTransaction = async (transactionId) => {
    try {
      await api.put(`/transactions/${transactionId}/reject`);
      toast.success('Transazione rifiutata.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel rifiuto');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const pendingTransactions = transactions.filter(t => t.status === 'pending');
  const confirmedTransactions = transactions.filter(t => t.status === 'confirmed');

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="flex items-center gap-3">
              <Logo linkTo="/" size="sm" />
              <div className="border-l border-slate-200 pl-3">
                <h1 className="font-semibold text-slate-900">{user?.name}</h1>
                <p className="text-xs text-slate-500">Il Mio Account</p>
              </div>
            </div>
            
            <div className="hidden md:flex items-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-lg">
                <Coins className="w-4 h-4 text-amber-600" />
                <span className="font-medium text-amber-700">{user?.total_cashback?.toFixed(0) || '0'} punti</span>
              </div>
              <Button variant="outline" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Esci
              </Button>
            </div>

            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-b border-slate-200 p-4">
          <div className="flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-lg mb-4">
            <Coins className="w-4 h-4 text-amber-600" />
            <span className="font-medium text-amber-700">Punti: {user?.total_cashback?.toFixed(0) || '0'}</span>
          </div>
          <Button variant="outline" className="w-full" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            Esci
          </Button>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Pending Transactions Alert */}
        {pendingTransactions.length > 0 && (
          <Card className="mb-8 border-amber-200 bg-amber-50">
            <CardContent className="p-6">
              <h3 className="font-semibold text-amber-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Transazioni da Confermare ({pendingTransactions.length})
              </h3>
              <div className="space-y-4">
                {pendingTransactions.map((tx) => (
                  <div key={tx.id} className="bg-white p-4 rounded-lg border border-amber-200">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <h4 className="font-semibold">{tx.company_name}</h4>
                        <p className="text-sm text-slate-600">{tx.offer_title}</p>
                        <p className="text-sm text-slate-500">
                          Speso: €{tx.amount_spent.toFixed(2)} | Punti: {tx.cashback_net.toFixed(0)}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleConfirmTransaction(tx.id)}
                          data-testid={`confirm-tx-${tx.id}`}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Conferma
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => handleRejectTransaction(tx.id)}
                          data-testid={`reject-tx-${tx.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Rifiuta
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600 font-medium">I Miei Punti</p>
                  <p className="text-3xl font-bold text-amber-700">{user?.total_cashback?.toFixed(0) || '0'}</p>
                  <p className="text-xs text-amber-500 mt-1">= €{((user?.total_cashback || 0) * (pointsCfg.user_point_value || 0.07)).toFixed(2)} in crediti</p>
                </div>
                <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center">
                  <Coins className="w-7 h-7 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Transazioni</p>
                  <p className="text-2xl font-bold text-slate-900">{confirmedTransactions.length}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <History className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Proposte Disponibili</p>
                  <p className="text-2xl font-bold text-slate-900">{offers.length}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Gift className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="coupons" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 w-full sm:w-auto justify-start overflow-x-auto flex-nowrap">
            <TabsTrigger value="coupons" data-testid="coupons-tab">
              <Ticket className="w-4 h-4 mr-2" />
              Le Mie Proposte
            </TabsTrigger>
            <TabsTrigger value="qrcode" data-testid="qrcode-tab">Il Mio QR Code</TabsTrigger>
            <TabsTrigger value="expenses" data-testid="expenses-tab">
              <Home className="w-4 h-4 mr-1" /> Spese Condo
            </TabsTrigger>
            <TabsTrigger value="family" data-testid="family-tab">
              <Users className="w-4 h-4 mr-1" /> Famiglia
            </TabsTrigger>
            <TabsTrigger value="offers" data-testid="offers-tab">Proposte</TabsTrigger>
            <TabsTrigger value="history" data-testid="history-tab">Storico</TabsTrigger>
          </TabsList>

          {/* Coupons Tab */}
          <TabsContent value="coupons">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Ticket className="w-5 h-5 text-emerald-600" />
                  Le Mie Proposte Salvati
                </CardTitle>
              </CardHeader>
              <CardContent>
                {myCoupons.length === 0 ? (
                  <div className="text-center py-12">
                    <Ticket className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-700 mb-2">Nessuna proposta attivata</h3>
                    <p className="text-slate-500 mb-6">Esplora le proposte e attivane una nuova!</p>
                    <Link to="/offers">
                      <Button className="bg-emerald-600 hover:bg-emerald-700">
                        <Gift className="w-4 h-4 mr-2" />
                        Scopri le Proposte
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {myCoupons.map((coupon) => (
                      <div key={coupon.id} className="border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
                        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-4 text-white">
                          <div className="flex items-center justify-between">
                            <Badge className="bg-white/20 text-white">{Math.round(coupon.points_awarded ?? coupon.discount_percentage ?? 0)} pt / €{Math.round(coupon.base_price ?? 100)}</Badge>
                            <span className="text-xs opacity-80">Scade: {new Date(coupon.end_date).toLocaleDateString('it-IT')}</span>
                          </div>
                          <h4 className="font-semibold mt-2">{coupon.title}</h4>
                        </div>
                        <div className="p-4 bg-white">
                          <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
                            <Store className="w-4 h-4" />
                            {coupon.company_name}
                          </div>
                          {coupon.company_city && (
                            <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
                              <MapPin className="w-4 h-4" />
                              {coupon.company_city}
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <Badge variant={coupon.status === 'active' ? 'default' : 'secondary'} className={coupon.status === 'active' ? 'bg-emerald-100 text-emerald-700' : ''}>
                              {coupon.status === 'active' ? 'Attivo' : coupon.status === 'reserved' ? 'In attesa di conferma' : coupon.status === 'used' ? 'Utilizzato' : 'Scaduto'}
                            </Badge>
                            {coupon.status === 'active' && coupon.qr_code && (
                              <Button
                                size="sm"
                                onClick={() => setCouponQrView(coupon)}
                                data-testid={`show-coupon-qr-${coupon.id}`}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white ml-auto"
                              >
                                <QrCode className="w-4 h-4 mr-1" /> Mostra QR
                              </Button>
                            )}
                            <Link to={`/offers/${coupon.offer_id}`}>
                              <Button variant="ghost" size="sm">
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* QR Code Tab */}
          <TabsContent value="qrcode">
            <Card>
              <CardHeader className="text-center">
                <CardTitle className="flex items-center justify-center gap-2">
                  <QrCode className="w-5 h-5" />
                  Il Tuo QR Code Personale
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center">
                <div className="p-8 bg-white rounded-2xl shadow-lg border border-slate-200">
                  {user?.qr_code ? (
                    <img 
                      src={`data:image/png;base64,${user.qr_code}`} 
                      alt="QR Code" 
                      className="w-64 h-64"
                      data-testid="user-qr-code"
                    />
                  ) : (
                    <QRCodeSVG 
                      value={`AIAC-USER:${user?.id}`}
                      size={256}
                      level="H"
                      data-testid="user-qr-code"
                    />
                  )}
                </div>
                <p className="mt-6 text-slate-600 text-center max-w-md">
                  Mostra questo QR Code all'esercente per registrare la tua transazione e accumulare punti.
                </p>
                <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <p className="text-sm text-amber-700 text-center">
                    <strong>Ricorda:</strong> Dopo la scansione, conferma la transazione entro 24h per ricevere i punti!
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Le mie residenze */}
            <Card className="mt-6">
              <CardHeader>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <CardTitle className="flex items-center gap-2">
                    <Home className="w-5 h-5 text-emerald-600" />
                    Le mie residenze ({userCondoIds.length})
                  </CardTitle>
                  <Button size="sm" onClick={() => setAddResidenceOpen(true)} data-testid="add-residence-btn">
                    <Plus className="w-4 h-4 mr-1" />
                    Aggiungi residenza
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {userCondoIds.length === 0 ? (
                  <p className="text-slate-500 text-sm">Non sei ancora residente di alcun condominio.</p>
                ) : (
                  <div className="space-y-2">
                    {userCondoIds.map((cid) => {
                      const condo = allCondos.find((c) => c.id === cid);
                      const status = userCondoApprovals[cid] || 'approved';
                      return (
                        <div key={cid} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg" data-testid={`residence-${cid}`}>
                          <div className="min-w-0">
                            <p className="font-medium truncate">{condo?.name || 'Condominio'}</p>
                            <p className="text-xs text-slate-500 truncate">{condo?.city || ''} {condo?.address ? `· ${condo.address}` : ''}</p>
                          </div>
                          <Badge className={
                            status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                            status === 'pending' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }>
                            {status === 'approved' ? 'Approvato' : status === 'pending' ? 'In attesa' : 'Rifiutato'}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-3">
                  Hai un appartamento in un altro condominio AIAC? Aggiungilo qui: dovrai attendere l'approvazione dell'amministratore.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="expenses">
            <Card data-testid="expenses-panel">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Home className="w-5 h-5 text-amber-600" />
                  Spese Condominiali - Applica i tuoi punti
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-amber-700 font-medium">Punti disponibili</p>
                      <p className="text-3xl font-bold text-amber-900" data-testid="user-available-points">{Math.round(availablePoints)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-amber-700 font-medium">Valore in €</p>
                      <p className="text-3xl font-bold text-emerald-700">€{(availablePoints * (pointsCfg.user_point_value || 0.07)).toFixed(2)}</p>
                      <p className="text-[10px] text-amber-600 mt-1">(1 punto = €{(pointsCfg.user_point_value || 0.07).toFixed(3)})</p>
                    </div>
                  </div>
                </div>
                {expenses.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Home className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                    <p>Nessuna spesa condominiale registrata dall'amministratore.</p>
                    <p className="text-xs mt-1">Appena il tuo condominio caricherà una rata, potrai usare i punti per pagarla.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {expenses.map((exp) => (
                      <div key={exp.id} className="p-4 border border-slate-200 rounded-lg bg-white" data-testid={`expense-row-${exp.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-slate-800 truncate">{exp.description}</p>
                            <p className="text-xs text-slate-500">{exp.period}</p>
                          </div>
                          <Badge className={
                            exp.status === 'paid' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                            exp.status === 'partially_paid' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                            'bg-slate-100 text-slate-700 border-slate-200'
                          }>
                            {exp.status === 'paid' ? 'Saldata' : exp.status === 'partially_paid' ? 'Parziale' : 'Da saldare'}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-3 text-sm">
                          <div>
                            <p className="text-xs text-slate-500">Totale</p>
                            <p className="font-semibold">€{exp.amount_eur.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Pagato con punti</p>
                            <p className="font-semibold text-emerald-700">€{exp.amount_paid_eur.toFixed(2)}</p>
                            <p className="text-[10px] text-slate-400">({Math.round(exp.points_applied)} pt)</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Residuo</p>
                            <p className="font-semibold text-amber-700">€{exp.amount_due_eur.toFixed(2)}</p>
                          </div>
                        </div>
                        {exp.status !== 'paid' && (
                          <div className="mt-3 flex justify-end">
                            <Button
                              onClick={() => openRedeem(exp)}
                              disabled={availablePoints <= 0}
                              data-testid={`redeem-btn-${exp.id}`}
                              className="bg-amber-500 hover:bg-amber-600 text-white"
                              size="sm"
                            >
                              <Coins className="w-4 h-4 mr-1" /> Applica Punti
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="family">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-emerald-600" />
                  {household?.name || 'Il mio nucleo familiare'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {!household ? (
                  <p className="text-slate-500">Caricamento…</p>
                ) : (
                  <>
                    {/* Family points */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200">
                        <p className="text-xs text-emerald-700 font-medium">Punti Famiglia</p>
                        <p className="text-3xl font-bold text-emerald-800 mt-1">{Math.round(household.total_points || 0)}</p>
                        <p className="text-xs text-emerald-600 mt-1">= €{((household.total_points || 0) * (pointsCfg.user_point_value || 0.07)).toFixed(2)} per spese condominiali</p>
                      </div>
                      <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                        <p className="text-xs text-slate-500 font-medium">Membri</p>
                        <p className="text-3xl font-bold text-slate-900 mt-1">{household.members?.length || 0}</p>
                      </div>
                      <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                        <p className="text-xs text-amber-700 font-medium">Capo Famiglia</p>
                        <p className="text-base font-semibold text-amber-900 mt-1 truncate">{household.head_name}</p>
                      </div>
                    </div>

                    {/* Referral link */}
                    <div className="p-4 rounded-xl border border-slate-200 bg-white">
                      <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                        <Share2 className="w-4 h-4 text-emerald-600" />
                        Invita familiari
                      </p>
                      <p className="text-sm text-slate-600 mt-1">
                        Condividi questo link con moglie, figli o genitori. I punti che guadagneranno si aggiungeranno al conto di famiglia.
                      </p>
                      <div className="mt-3 flex items-center gap-2">
                        <code className="flex-1 px-3 py-2 rounded-lg bg-slate-100 text-sm font-mono truncate">
                          {window.location.origin}/register?ref={household.referral_code}
                        </code>
                        <Button size="sm" onClick={copyReferralLink} data-testid="copy-referral-btn">
                          <Copy className="w-4 h-4 mr-1" /> Copia
                        </Button>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <Button size="sm" variant="outline" onClick={() => {
                          const link = `${window.location.origin}/register?ref=${household.referral_code}`;
                          const text = `Unisciti alla mia famiglia su AIAC Card per accumulare punti insieme: ${link}`;
                          window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                        }}>
                          <Share2 className="w-4 h-4 mr-1" /> WhatsApp
                        </Button>
                        <Badge variant="outline" className="font-mono">Codice: {household.referral_code}</Badge>
                      </div>
                    </div>

                    {/* Members */}
                    <div>
                      <p className="text-sm font-semibold text-slate-900 mb-3">Membri della famiglia</p>
                      <div className="space-y-2">
                        {household.members?.map((m) => (
                          <div key={m.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
                                <User className="w-4 h-4 text-emerald-700" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{m.name} {m.is_head && <Badge className="ml-1 bg-amber-200 text-amber-800">Capo famiglia</Badge>}</p>
                                <p className="text-xs text-slate-500">{m.email}</p>
                              </div>
                            </div>
                            <Badge className="bg-emerald-100 text-emerald-700">
                              {Math.round(m.total_cashback || 0)} punti
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Offers Tab */}
          <TabsContent value="offers">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {offers.length === 0 ? (
                <p className="text-slate-500 col-span-full text-center py-8">Nessuna proposta disponibile</p>
              ) : (
                offers.map((offer) => (
                  <Card key={offer.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-0">
                      <div className="h-40 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-t-lg flex flex-col items-center justify-center">
                        <span className="text-4xl font-bold text-white">{offer.discount_percentage}%</span>
                        <span className="text-sm font-semibold text-white/90 tracking-wide">punti</span>
                      </div>
                      <div className="p-6">
                        <Badge className="mb-2 bg-slate-100 text-slate-700">{offer.category}</Badge>
                        <h3 className="font-semibold text-lg mb-2">{offer.title}</h3>
                        <p className="text-sm text-slate-600 mb-4 line-clamp-2">{offer.description}</p>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Store className="w-4 h-4" />
                          <span>{offer.company_name}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>Storico Transazioni</CardTitle>
              </CardHeader>
              <CardContent>
                {confirmedTransactions.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">Nessuna transazione completata</p>
                ) : (
                  <div className="space-y-4">
                    {confirmedTransactions.map((tx) => (
                      <div 
                        key={tx.id} 
                        className="p-4 border border-slate-200 rounded-lg flex items-center justify-between"
                      >
                        <div>
                          <h4 className="font-semibold">{tx.company_name}</h4>
                          <p className="text-sm text-slate-500">{tx.offer_title}</p>
                          <p className="text-xs text-slate-400">
                            {new Date(tx.confirmed_at || tx.created_at).toLocaleDateString('it-IT')}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-500">Speso: €{tx.amount_spent.toFixed(2)}</p>
                          <p className="font-bold text-amber-600">+{tx.cashback_net.toFixed(0)} punti</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Redeem Points Dialog */}
      <Dialog open={redeemDialog.open} onOpenChange={(o) => { if (!o) { setRedeemDialog({ open: false, expense: null }); setRedeemPoints(''); } }}>
        <DialogContent className="max-w-md" data-testid="redeem-points-dialog">
          <DialogHeader>
            <DialogTitle>Applica punti alla spesa</DialogTitle>
          </DialogHeader>
          {redeemDialog.expense && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-semibold text-slate-800">{redeemDialog.expense.description}</p>
                <p className="text-xs text-slate-500">{redeemDialog.expense.period}</p>
                <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                  <div><span className="text-slate-500">Totale:</span> <b>€{redeemDialog.expense.amount_eur.toFixed(2)}</b></div>
                  <div><span className="text-slate-500">Residuo:</span> <b className="text-amber-700">€{redeemDialog.expense.amount_due_eur.toFixed(2)}</b></div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 bg-emerald-50 rounded-lg">
                  <p className="text-slate-500">Punti disponibili</p>
                  <p className="text-xl font-bold text-emerald-700" data-testid="available-points">{availablePoints.toFixed(0)}</p>
                  <p className="text-xs text-emerald-600">= €{(availablePoints * (pointsCfg.user_point_value || 0.07)).toFixed(2)}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-slate-500">Valore punto</p>
                  <p className="text-xl font-bold text-slate-700">€{(pointsCfg.user_point_value || 0.07).toFixed(3)}</p>
                  <p className="text-xs text-slate-500">1 punto</p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Punti da applicare</label>
                <input
                  type="number"
                  min="1"
                  max={availablePoints}
                  step="1"
                  value={redeemPoints}
                  onChange={(e) => setRedeemPoints(e.target.value)}
                  data-testid="redeem-points-input"
                  className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                {redeemPoints && parseFloat(redeemPoints) > 0 && (
                  <p className="text-xs text-slate-500 mt-1">
                    Verranno detratti dalla spesa: <b className="text-emerald-700">€{(parseFloat(redeemPoints) * (pointsCfg.user_point_value || 0.07)).toFixed(2)}</b>
                  </p>
                )}
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setRedeemDialog({ open: false, expense: null }); setRedeemPoints(''); }}>Annulla</Button>
                <Button disabled={redeeming} onClick={handleRedeem} data-testid="confirm-redeem-btn" className="bg-amber-500 hover:bg-amber-600 text-white">Applica Punti</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Coupon QR Dialog - show scannable QR of the specific coupon */}
      <Dialog open={!!couponQrView} onOpenChange={(o) => { if (!o) setCouponQrView(null); }}>
        <DialogContent className="max-w-sm" data-testid="coupon-qr-dialog">
          <DialogHeader>
            <DialogTitle>{couponQrView?.title}</DialogTitle>
            <DialogDescription>Mostra questo QR all'esercente per registrare la transazione.</DialogDescription>
          </DialogHeader>
          {couponQrView && (
            <div className="space-y-3">
              <div className="text-center">
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                  {Math.round(couponQrView.points_awarded ?? couponQrView.discount_percentage ?? 0)} punti / €{Math.round(couponQrView.base_price ?? 100)}
                </Badge>
                <p className="text-sm text-slate-500 mt-2">{couponQrView.company_name}{couponQrView.company_city ? ` · ${couponQrView.company_city}` : ''}</p>
              </div>
              {couponQrView.qr_code ? (
                <div className="p-4 bg-white border-4 border-emerald-100 rounded-2xl flex items-center justify-center">
                  <img
                    src={`data:image/png;base64,${couponQrView.qr_code}`}
                    alt="QR proposta"
                    className="w-64 h-64"
                    data-testid="coupon-qr-image"
                  />
                </div>
              ) : (
                <p className="text-center text-sm text-slate-500">QR non disponibile</p>
              )}
              <p className="text-xs text-center text-slate-500">
                Mostra questo QR all'esercente. Sarà lui a inquadrarlo per registrare la transazione — non dovrai selezionare la proposta.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Residence Dialog */}
      <Dialog open={addResidenceOpen} onOpenChange={(o) => { setAddResidenceOpen(o); if (!o) setSelectedNewCondo(''); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Aggiungi una residenza</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Seleziona il condominio dove hai un appartamento. La richiesta dovrà essere approvata dall'amministratore di quel condominio.
            </p>
            <div>
              <Select value={selectedNewCondo} onValueChange={setSelectedNewCondo}>
                <SelectTrigger data-testid="select-new-residence"><SelectValue placeholder="Seleziona condominio" /></SelectTrigger>
                <SelectContent>
                  {allCondos
                    .filter((c) => !userCondoIds.includes(c.id))
                    .map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}{c.city ? ` · ${c.city}` : ''}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setAddResidenceOpen(false)}>Annulla</Button>
              <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={!selectedNewCondo} onClick={handleAddResidence} data-testid="confirm-add-residence-btn">
                Invia richiesta
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserDashboard;
